import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatCurrency } from '@/utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-purple-400 text-xl">Cargando órdenes...</div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" data-testid="no-orders">
        <Package size={80} className="text-gray-600 mb-6" />
        <h2 className="text-2xl font-bold text-gray-400 mb-4">No tienes órdenes aún</h2>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="orders-page">
      <div className="container mx-auto px-4 max-w-6xl">
        <h1 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" data-testid="orders-title">
          Mis Órdenes
        </h1>

        <div className="space-y-6">
          {orders.map((order) => {
            const statusColors = {
              pending: 'bg-yellow-600/20 border-yellow-600/50 text-yellow-400',
              processing: 'bg-blue-600/20 border-blue-600/50 text-blue-400',
              shipped: 'bg-purple-600/20 border-purple-600/50 text-purple-400',
              delivered: 'bg-green-600/20 border-green-600/50 text-green-400',
              cancelled: 'bg-red-600/20 border-red-600/50 text-red-400'
            };
            
            const statusLabels = {
              pending: 'Pendiente',
              processing: 'Procesando',
              shipped: 'Enviado',
              delivered: 'Entregado',
              cancelled: 'Cancelado',
              completed: 'Completado'
            };
            
            return (
            <div key={order.id} className="glass rounded-xl p-6 fade-in" data-testid="order-card">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 pb-6 border-b border-purple-900/30">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2" data-testid="order-id">Orden #{order.id.slice(0, 8)}</h3>
                  <div className="flex items-center text-gray-400 text-sm">
                    <Calendar size={16} className="mr-2" />
                    <span data-testid="order-date">
                      {format(new Date(order.created_at), "d 'de' MMMM, yyyy", { locale: es })}
                    </span>
                  </div>
                  {order.tracking_number && (
                    <div className="flex items-center text-purple-400 text-sm mt-2">
                      <Package size={16} className="mr-2" />
                      <span>Tracking: {order.tracking_number}</span>
                    </div>
                  )}
                </div>
                
                <div className="mt-4 md:mt-0">
                  <div className={`inline-block px-4 py-2 border rounded-full font-bold ${statusColors[order.status] || 'bg-gray-600/20 border-gray-600/50 text-gray-400'}`} data-testid="order-status">
                    {statusLabels[order.status] || order.status}
                  </div>
                </div>
              </div>

              {order.delivery_type && (
                <div className="mb-4 p-4 bg-purple-600/10 border border-purple-600/30 rounded-lg">
                  <p className="text-white font-bold mb-1">
                    {order.delivery_type === 'pickup' ? '📦 Retiro en tienda' : '🚚 Envío a domicilio'}
                  </p>
                  {order.delivery_type === 'delivery' && order.delivery_address && (
                    <p className="text-gray-400 text-sm">
                      {order.delivery_address}, {order.delivery_city}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-4 mb-6">
                {order.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center" data-testid="order-item">
                    <div>
                      <h4 className="text-white font-medium" data-testid="order-item-name">{item.product_name}</h4>
                      <p className="text-gray-400 text-sm" data-testid="order-item-quantity">Cantidad: {item.quantity}</p>
                    </div>
                    <div className="text-purple-400 font-bold" data-testid="order-item-price">
                      ${(item.price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-purple-900/30 pt-4 flex justify-between items-center">
                <span className="text-xl font-bold text-white">Total</span>
                <span className="text-2xl font-bold text-purple-400" data-testid="order-total">${order.total.toFixed(2)}</span>
              </div>
            </div>
          );
          })}
        </div>
      </div>
    </div>
  );
};

export default Orders;